import React, { useCallback, useRef } from 'react';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import useUser from '@vl-core/hooks/useUser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { Form, Modal, Result, Select } from 'ALIASED-antd';
import { SessionForm } from '@components/Clinicians/session-form';
import useUserNextAppointments from '@hooks/useUserNextAppointments';
import { useCancelledAppointments } from '@hooks/useCancelledAppointments';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

const useCancelAppointment = () => {
  const user = useUser();
  const dispatch = useDispatch();
  const router = useRouter();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { appointment } = useSessionAppointment();
  const form = Form.useFormInstance<SessionForm>();
  const [, invalidateAppointments] = useUserNextAppointments(10);
  const { setCancelledAppointments } = useCancelledAppointments();
  const cancelReasonModalRef = useRef<ReturnType<typeof Modal.confirm> | null>(null);

  // Unchanges
  const returnToAppointmentsPage = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['clinicianAppointment']
    });
    router.push('/clinician/appointments').then();
  }, [router, queryClient]);

  const { data: cancelOptions } = useQuery({
    queryKey: ['cancelReasons'],
    queryFn: async () => {
      return dispatch(actions.rescheduleCancelMeta());
    },
    select: (data) => {
      const cancelReasonList = data.payload[0].appt_cancel_reasons;
      return cancelReasonList?.map((item: { appt_cancel_code: string; appt_cancel_desc: string }) => ({
        value: item.appt_cancel_code,
        label: item.appt_cancel_desc
      }));
    }
  });

  const { mutateAsync: cancelAppointment, isPending: isCancelAppointmentLoading } = useMutation({
    mutationFn: async (cancelReason: string) => {
      const reasonResponse = await dispatch(
        actions.rescheduleCancel({
          user_guid: user.user_guid,
          appt_id: appointment.appointment_id.toString(),
          reason: cancelReason
        }),
        false
      );
      if (reasonResponse.type === actionTypes.RESCHEDULE_CANCEL_SUCCESS) return { status: 200 };
      const errorBody = await reasonResponse.error?.json?.().catch(() => null);
      throw new Error(errorBody?.message || reasonResponse.error?.message || 'Failed to cancel appointment');
    },
    onSuccess: () => {
      Modal.success({
        centered: true,
        icon: <></>,
        width: '30rem',
        title: (
          <Result
            status="info"
            title={t('Appointment Cancelled')}
            subTitle="This appointment has now been cancelled and the member will be contacted by our admin team. A rescheduled appointment may be created when checks have been created"
          />
        ),
        modalRender: (originalNode) => <div data-testid="appointment-cancelled-modal">{originalNode}</div>,
        onOk: () => {
          invalidateAppointments();
          setCancelledAppointments((a) => ({ ...a, [appointment.appointment_id]: true }));
          returnToAppointmentsPage();
        },
        okText: 'Go to Appointments',
        okButtonProps: { style: { display: 'block', margin: '0 auto 2rem auto' } }
      });
    },
    onError: (error: Error) => {
      cancelReasonModalRef.current?.destroy();
      Modal.error({
        title: t('Could not cancel appointment'),
        content: error.message || 'Failed to cancel appointment',
        centered: true,
        modalRender: (originalNode) => <div data-testid="cancel-appointment-modal">{originalNode}</div>
      });
    }
  });

  const onCancelAppointmentConfirm = useCallback(() => {
    cancelReasonModalRef.current = Modal.confirm({
      title: t('Cancel Appointment'),
      modalRender: (originalNode) => <div data-testid="cancel-appointment-modal">{originalNode}</div>,
      centered: true,
      style: { minWidth: '20vw' },
      content: (
        <Form.Item
          name="cancellation_reason"
          label={t('Why are you cancelling this appointment?_3')}
          labelCol={{ span: 24 }}
        >
          <Select
            placeholder={t('Select')}
            options={cancelOptions}
            data-testid="appointment-cancel-reason-select"
            onChange={(value) => form.setFieldValue('cancellation_reason', value)}
          />
        </Form.Item>
      ),
      okText: 'Confirm',
      okButtonProps: { loading: isCancelAppointmentLoading, 'data-testid': 'confirm-cancel-appointment' },
      onOk: async () => {
        const reason = form.getFieldValue('cancellation_reason');
        if (!reason) return Promise.reject();
        return cancelAppointment(reason);
      }
    });
  }, [form, cancelAppointment, cancelOptions, isCancelAppointmentLoading]);

  const onCancelAppointment = useCallback(() => {
    if (appointment.appointment_completed) {
      Modal.error({
        title: t('Unable to cancel appointment'),
        modalRender: (originalNode) => <div data-testid="unable-to-transfer-appointment-modal">{originalNode}</div>,
        content: 'This appointment has already been completed and cannot be cancelled.',
        okText: 'Go to Appointments',
        closable: true,
        centered: true,
        onOk: () => {
          returnToAppointmentsPage();
        }
      });
      return;
    }
    onCancelAppointmentConfirm();
  }, [appointment, onCancelAppointmentConfirm, returnToAppointmentsPage]);

  return {
    onCancelAppointment
  };
};

export { useCancelAppointment };
